import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { preFixImgUrl } from '@/utils'
import { Link, useSearchParams } from 'react-router-dom'
import { LeftOutlined, RightOutlined, StarFilled } from '@ant-design/icons'
import { SparkleTitle } from '../../../HanedaShop'
import useModel, {
  COUPON_STATIC_TEXT,
  type CouponQuickLinkItem,
  type TempCouponStore,
  TEMP_COUPON_PAGE_SIZE,
  TEMP_COUPON_STORES,
  formatCouponParticipants,
  getCouponLocaleText,
} from './useModel'

const btnGradient = {
  background: 'linear-gradient(90deg, #5A6EFF 0%, #9A66FF 100%)',
} as const

/** 分页与 URL 同步：?page=1、?page=2，便于从详情页「返回」仍停留在原页 */
const readPageIndexFromParams = (pageParam: string | null, totalPages: number): number => {
  if (pageParam == null) return 0
  const n = parseInt(pageParam, 10)
  if (Number.isNaN(n) || n < 1) return 0
  const p0 = n - 1
  if (totalPages <= 0) return 0
  return Math.min(Math.max(0, p0), totalPages - 1)
}

const TempCouponCard = ({ store }: { store: TempCouponStore }) => {
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const title = `${store.merchantName}${getCouponLocaleText(COUPON_STATIC_TEXT.touristExclusiveSuffix, language)}`
  const primaryCategory = getCouponLocaleText(store.primaryCategory, language)
  const secondaryCategory = getCouponLocaleText(store.secondaryCategory, language)
  const claimToday = getCouponLocaleText(COUPON_STATIC_TEXT.claimToday, language)
  const participants = formatCouponParticipants(
    store.participantCount,
    COUPON_STATIC_TEXT.participantsSuffix,
    language,
  )

  return (
    <article className="flex h-full overflow-hidden flex-col bg-white rounded-[8px]">
      <Link
        to={`/temp-coupon-detail?id=${encodeURIComponent(store.id)}`}
        className="block overflow-hidden bg-[#F4F4F7]"
        aria-label={title}
      >
        <img
          src={preFixImgUrl(store.listImageFile)}
          alt=""
          className="aspect-[16/9] w-full object-cover transition-transform duration-300 hover:scale-[1.02]"
        />
      </Link>
      <div className="flex flex-1 flex-col px-[26px] pb-[36px] pt-[24px]">
        <div className="flex items-center gap-[12px] text-[20px] leading-none text-[#8E8E96]">
          <span>{primaryCategory}</span>
          <span className="h-[18px] w-px bg-[#D7D7DE]" aria-hidden />
          <span>{secondaryCategory}</span>
        </div>
        <Link
          to={`/temp-coupon-detail?id=${encodeURIComponent(store.id)}`}
          className="mt-[16px] min-h-[64px] text-[28px] leading-[32px] font-bold text-[#050505] no-underline line-clamp-2"
        >
          {title}
        </Link>
        <div className="mt-[16px] inline-flex h-[34px] w-fit items-center rounded-[4px] bg-[#F5F5F5] px-[16px] text-[20px] leading-none text-[#777777]">
          {claimToday}
        </div>
        <div className="mt-[20px] flex min-w-0 items-center text-[16px] leading-none text-[#707070]">
          <StarFilled className="mr-[7px] shrink-0 text-[17px] !text-[#F09B0A]" />
          <span className="mr-[8px] shrink-0 text-[20px] font-bold text-[#F09B0A]">{store.rating}</span>
          <span className="shrink-0">({store.reviewCount})</span>
          <span className="mx-[5px] shrink-0">·</span>
          <span className="min-w-0 truncate">{participants}</span>
        </div>
        <Link
          to={`/temp-coupon-detail?id=${encodeURIComponent(store.id)}`}
          className="mt-[30px] inline-flex h-[64px] w-full items-center justify-center rounded-full text-[22px] font-medium text-white no-underline transition-opacity hover:opacity-90"
          style={btnGradient}
        >
          <LangText lngKey="e88bc06f">{t('e88bc06f')}</LangText>
        </Link>
      </div>
    </article>
  )
}

const TempCouponList = () => {
  const { t } = useTranslation()
  const { quickLinks } = useModel()
  const [searchParams, setSearchParams] = useSearchParams()

  const { page, totalPages, pageItems } = useMemo(() => {
    const total = Math.ceil(TEMP_COUPON_STORES.length / TEMP_COUPON_PAGE_SIZE)
    const p0 = readPageIndexFromParams(searchParams.get('page'), total)
    const start = p0 * TEMP_COUPON_PAGE_SIZE
    return {
      page: p0,
      totalPages: total,
      pageItems: TEMP_COUPON_STORES.slice(start, start + TEMP_COUPON_PAGE_SIZE),
    }
  }, [searchParams])

  const goToPage = useCallback(
    (p0: number) => {
      if (p0 < 0 || p0 >= totalPages) return
      if (p0 === 0) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev)
            next.delete('page')
            return next
          },
          { replace: true },
        )
      } else {
        setSearchParams({ page: String(p0 + 1) }, { replace: true })
      }
    },
    [setSearchParams, totalPages],
  )

  return (
    <div className="bg-[#F8F6FF]">
      {/* Hero（样式对齐 SagawaExp） */}
      <div
        className="relative min-h-[480px] flex flex-col items-center justify-center px-6 py-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${preFixImgUrl('f01726b69235efd3e50de29c1ae25c36.png')})` }}
      >
        <div className="flex relative z-10 flex-col items-center mx-auto max-w-3xl text-center">
          <LangText lngKey="cf14d89b" className="text-white text-[40px] md:text-[44px] font-bold leading-tight px-2" />
          <LangText lngKey="075dace4" className="text-white text-[17px] md:text-[18px] leading-relaxed mt-6 px-2" />
          {/* <div className="flex flex-wrap gap-5 justify-center mt-10">
            <div
              className="h-[48px] min-w-[140px] px-10 flex items-center justify-center rounded-full text-white text-[17px] cursor-pointer transition-opacity hover:opacity-90"
              style={{ background: 'linear-gradient(270deg, #A75AFF 0%, #4B51FF 100%)' }}
              onClick={() => navigate('/mine-coupon')}
            >
              <LangText lngKey="65d24316" className="text-white" />
            </div>
          </div> */}
        </div>
      </div>

      <section className="pt-10 pb-14 md:pt-16 md:pb-20">
        <div className="mx-auto w-full max-w-[1480px] px-6 md:px-10">
          <SparkleTitle lngKey="89188052" purpleTitle />
          <div className="grid grid-cols-3 gap-4 px-16 mt-10">
            {pageItems.map((store) => (
              <TempCouponCard key={store.id} store={store} />
            ))}
          </div>

          {totalPages > 1 ? (
            <div className="flex gap-6 justify-center items-center mt-12">
              <button
                type="button"
                aria-label="上一页"
                className="flex size-12 items-center justify-center rounded-full border-0 bg-white text-[#5A5EFA] shadow-md transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-30"
                disabled={page <= 0}
                onClick={() => goToPage(page - 1)}
              >
                <LeftOutlined className="text-lg" />
              </button>
              <span className="min-w-[3rem] text-center text-sm text-[#666]">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                aria-label="下一页"
                className="flex size-12 items-center justify-center rounded-full border-0 bg-white text-[#5A5EFA] shadow-md transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-30"
                disabled={page >= totalPages - 1}
                onClick={() => goToPage(page + 1)}
              >
                <RightOutlined className="text-lg" />
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {/* <div
        className="relative min-h-[360px] flex flex-col items-center justify-center px-6 py-16 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${preFixImgUrl('2a9fda00bf273aa46cd435ba98d345e9.png')})` }}
      >
        <div className="flex relative z-10 flex-col items-center max-w-3xl text-center">
          <div
            className="h-[52px] min-w-[200px] px-10 flex items-center justify-center rounded-full text-white text-[18px] cursor-pointer transition-opacity hover:opacity-90"
            style={{ background: 'linear-gradient(270deg, #A75AFF 0%, #4B51FF 100%)' }}
            onClick={() => navigate('/mine-coupon')}
          >
            <LangText lngKey="4696e630">{t('4696e630')}</LangText>
          </div>
        </div>
      </div> */}

      {/* 快捷入口：用户条款 /items，注意事项 /coupon-precautions，联系我们 mailto */}
      <section className="pt-12 pb-16 bg-[#fff]">
        <div className="px-6 mx-auto w-8xl md:px-10">
          <div className="grid grid-cols-3 gap-3 w-full sm:gap-4 md:gap-6">
            {quickLinks.map((item: CouponQuickLinkItem) => {
              const cardClass =
                'flex flex-col gap-4 items-center min-w-0 text-center cursor-pointer transition-opacity hover:opacity-90 text-inherit no-underline'
              const inner = (
                <>
                  <span className="w-[88px] h-[88px] flex items-center justify-center">
                    <img src={item.iconImg} alt="" className="object-contain max-w-full max-h-full" />
                  </span>
                  <LangText lngKey={item.key} className="text-[18px] text-[#333333] font-bold">
                    {t(item.key)}
                  </LangText>
                </>
              )
              if ('href' in item) {
                return (
                  <a key={item.key} href={item.href} className={cardClass}>
                    {inner}
                  </a>
                )
              }
              return (
                <Link key={item.key} to={item.to} className={cardClass}>
                  {inner}
                </Link>
              )
            })}
          </div>
        </div>
      </section>
    </div>
  )
}

export default TempCouponList
